import React, { useState } from 'react';
import { Link2, TrendingUp, DollarSign, Share2, Copy, ChevronRight, Wallet, Clock, CheckCircle2, Send, User, Mail, Phone, Instagram, Users, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Referrals = () => {
  const [showApplication, setShowApplication] = useState(false);
  const [showStatusCheck, setShowStatusCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    social_media: '',
    team_name: '',
    reason: '',
    how_promote: ''
  });
  const [statusEmail, setStatusEmail] = useState('');
  const [statusResult, setStatusResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({ ...prev, [name]: value }));
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(applicationData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.status === 'approved') {
          toast.success('You already have an approved referral account!');
          setStatusResult(data);
          setShowApplication(false);
          setShowStatusCheck(true);
        } else {
          toast.success(data.message);
          setShowApplication(false);
          setApplicationData({ name: '', email: '', phone: '', social_media: '', team_name: '', reason: '', how_promote: '' });
        }
      } else {
        toast.error(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/referrals/check/${encodeURIComponent(statusEmail)}`);
      const data = await response.json();
      
      setStatusResult(data);
      
      if (data.status === 'not_found') {
        toast.error('No application found for this email');
      } else if (data.status === 'approved') {
        toast.success('Your application is approved! Your referral link is ready.');
      } else if (data.status === 'pending') {
        toast.info('Your application is still under review.');
      } else if (data.status === 'rejected') {
        toast.error('Your application was not approved.');
      }
    } catch (error) {
      toast.error('Failed to check status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard!');
  };

  const howItWorks = [
    {
      step: 1,
      title: 'Apply to Join',
      description: 'Fill out the application form and tell us about yourself',
      icon: User,
    },
    {
      step: 2,
      title: 'Get Approved',
      description: "We'll review your application within 48 hours",
      icon: CheckCircle2,
    },
    {
      step: 3,
      title: 'Share Your Link',
      description: 'Get your unique referral link and share it everywhere',
      icon: Share2,
    },
    {
      step: 4,
      title: 'Earn 15%',
      description: 'Earn 15% commission on every sale from your referrals',
      icon: DollarSign,
    },
  ];

  return (
    <section id="referrals" className="section-padding relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#131416] via-[#0a0b0c] to-[#131416]" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#00d4aa]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#00d4aa]/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#00d4aa] text-sm font-bold uppercase tracking-widest mb-4">
            Referral Program
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4">
            Earn <span className="text-[#00d4aa]">15%</span> Commission
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Join our referral program and earn 15% of every sale made through your unique link.
            Share GWINS with your team and community!
          </p>
        </div>

        {/* How It Works */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {howItWorks.map((item, index) => (
            <div key={item.step} className="relative">
              {/* Connector line */}
              {index < howItWorks.length - 1 && (
                <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#00d4aa]/30 to-transparent" />
              )}
              
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-[#00d4aa]/10 border border-[#00d4aa]/30 flex items-center justify-center mx-auto mb-4 relative">
                  <item.icon className="h-8 w-8 text-[#00d4aa]" />
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#00d4aa] text-[#0a0b0c] font-bold text-sm flex items-center justify-center">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-white text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Commission Highlight */}
        <div className="bg-gradient-to-r from-[#00d4aa]/20 to-[#00d4aa]/5 rounded-2xl p-8 mb-12 border border-[#00d4aa]/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-3xl font-black text-white mb-2">15% Commission on Every Sale</h3>
              <p className="text-white/70">No caps, no limits. The more you share, the more you earn!</p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-[#00d4aa] text-5xl font-black">15%</p>
              <p className="text-white/60 text-sm">Per Sale</p>
            </div>
          </div>
        </div>

        {/* Application Form / Status Check */}
        {!showApplication && !showStatusCheck ? (
          <div className="bg-[#131416] rounded-2xl border border-white/10 p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Start Earning?</h3>
              <p className="text-white/60 mb-8 max-w-xl mx-auto">
                Apply to join our referral program and get your unique link. 
                Share with your team, on social media, and at events!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setShowApplication(true)}
                  className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-8"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Apply Now
                </Button>
                <Button
                  onClick={() => setShowStatusCheck(true)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 rounded-full px-8"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Check My Status
                </Button>
                <Button
                  onClick={() => window.location.href = '/referral-dashboard'}
                  variant="outline"
                  className="border-[#00d4aa]/30 text-[#00d4aa] hover:bg-[#00d4aa]/10 rounded-full px-8"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  My Dashboard
                </Button>
              </div>
            </div>
          </div>
        ) : showApplication ? (
          /* Application Form */
          <div className="bg-[#131416] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Referral Program Application</h3>
              <Button
                variant="ghost"
                onClick={() => setShowApplication(false)}
                className="text-white/60 hover:text-white"
              >
                ← Back
              </Button>
            </div>
            
            <form onSubmit={handleApply} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      name="name"
                      value={applicationData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      name="email"
                      type="email"
                      value={applicationData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      name="phone"
                      value={applicationData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Social Media Handle
                  </label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      name="social_media"
                      value={applicationData.social_media}
                      onChange={handleInputChange}
                      placeholder="@yourhandle"
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Team Name (if applicable)
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    name="team_name"
                    value={applicationData.team_name}
                    onChange={handleInputChange}
                    placeholder="Your paintball team name"
                    className="pl-10 bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Why do you want to join our referral program?
                </label>
                <textarea
                  name="reason"
                  value={applicationData.reason}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself and why you'd be a great ambassador..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-3 resize-none focus:border-[#00d4aa]/50 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  How will you promote GWINS Gear?
                </label>
                <textarea
                  name="how_promote"
                  value={applicationData.how_promote}
                  onChange={handleInputChange}
                  placeholder="Social media, at events, team sponsorships, etc..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-3 resize-none focus:border-[#00d4aa]/50 focus:outline-none"
                />
              </div>
              
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          /* Status Check */
          <div className="bg-[#131416] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Check Application Status</h3>
              <Button
                variant="ghost"
                onClick={() => { setShowStatusCheck(false); setStatusResult(null); }}
                className="text-white/60 hover:text-white"
              >
                ← Back
              </Button>
            </div>
            
            {!statusResult ? (
              <form onSubmit={handleCheckStatus} className="space-y-6">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Enter the email you used to apply
                  </label>
                  <div className="flex gap-4">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                      <Input
                        type="email"
                        value={statusEmail}
                        onChange={(e) => setStatusEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="pl-10 bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-6"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                    </Button>
                  </div>
                </div>
              </form>
            ) : statusResult.status === 'approved' ? (
              /* Approved - Show Dashboard */
              <div className="space-y-6">
                <div className="bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="h-8 w-8 text-[#00d4aa]" />
                    <div>
                      <h4 className="text-white font-bold text-lg">Application Approved!</h4>
                      <p className="text-white/60">Welcome to the GWINS referral program</p>
                    </div>
                  </div>
                  
                  <div className="bg-[#0a0b0c] rounded-lg p-4 mb-4">
                    <p className="text-white/60 text-sm mb-2">Your Unique Referral Link:</p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 text-[#00d4aa] text-sm break-all">
                        {statusResult.referral_link}
                      </code>
                      <Button
                        onClick={() => handleCopyLink(statusResult.referral_link)}
                        size="sm"
                        className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#0a0b0c] rounded-lg p-4">
                      <p className="text-white/60 text-sm">Your Code</p>
                      <p className="text-white font-bold text-xl">{statusResult.referral_code}</p>
                    </div>
                    <div className="bg-[#0a0b0c] rounded-lg p-4">
                      <p className="text-white/60 text-sm">Commission Rate</p>
                      <p className="text-[#00d4aa] font-bold text-xl">{statusResult.commission_rate}%</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-white/60 text-sm text-center">
                  Share your link and earn 15% on every sale! Track your earnings by checking back here.
                </p>
              </div>
            ) : statusResult.status === 'pending' ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
                <Clock className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h4 className="text-white font-bold text-lg mb-2">Application Under Review</h4>
                <p className="text-white/60">
                  We received your application from <span className="text-white">{statusResult.name}</span>.<br />
                  We'll review it and get back to you within 48 hours!
                </p>
              </div>
            ) : (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
                <p className="text-white/60">No application found or application was not approved.</p>
                <Button
                  onClick={() => { setShowStatusCheck(false); setShowApplication(true); }}
                  className="mt-4 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full"
                >
                  Apply Now
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Terms Link */}
        <div className="text-center mt-8">
          <a href="/referral-terms" className="text-white/40 hover:text-[#00d4aa] text-sm transition-colors">
            View Referral Program Terms & Conditions
          </a>
        </div>
      </div>
    </section>
  );
};

export default Referrals;
